/**
 * MIGRATE EVENT SOURCE → EVENT NAME (Admin Tool)
 *
 * Historical context: on 2026-01-20 (commit a79b27c "updated collection
 * info") the polish schema was expanded from 4 releaseTypes to 11.
 * As part of unifying field naming to a `{type}Name` pattern
 * (boxName, groupName, charityName, conventionName, etc.), the event
 * release type's field got renamed from `eventSource` to `eventName`.
 *
 * That refactor added a fallback read + auto-migrate-on-save in
 * PolishDetailModal, but never backfilled the ~966 legacy items that
 * still have `eventSource` populated and no `eventName`. This admin
 * page runs the backfill in bulk.
 *
 * Verified against user data: `eventSource` and `eventName` hold the
 * same conceptual value (recurring event names — "Polish Pick Up",
 * "Hella Handmade Creations"). Boxes use a separate `boxName` field.
 * Migration is safe: same field, different name.
 *
 * Non-destructive by default: copies eventSource → eventName only
 * where eventName is empty, leaves eventSource in place for audit.
 * A follow-up "delete eventSource" pass can happen post-audit.
 *
 * Auth-gated on `isAdmin === true`.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  doc,
  getDoc,
  collectionGroup,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft, RefreshCw } from 'lucide-react';

/** Firestore batch cap, padded for headroom against downstream triggers. */
const BATCH_LIMIT = 450;

export default function MigrateEventSource() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [dryRun, setDryRun] = useState(true);
  /** When on, also nulls out `eventSource` on items we migrate. Off by
   *  default — first pass just copies to eventName. Once the app has
   *  been verified to only READ from eventName (and every DetailModal
   *  auto-migrates on save), a second pass with this toggle on can
   *  clean up the legacy field. */
  const [alsoNullEventSource, setAlsoNullEventSource] = useState(false);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsub();
  }, []);

  const say = (m: string) => {
    setLog((prev) => [...prev, m]);
    console.log('[MigrateEventSource]', m);
  };

  const run = async () => {
    setRunning(true);
    setLog([]);
    say(`${dryRun ? '[DRY-RUN] ' : ''}Scanning user items…`);
    if (alsoNullEventSource) {
      say('  Also nulling out `eventSource` on migrated items.');
    }
    try {
      const snap = await getDocs(collectionGroup(db, 'items'));
      let scanned = 0;
      let skippedNonUser = 0;
      let skippedDeleted = 0;
      let hasBothSame = 0;
      let hasBothDifferent = 0;
      let hasOnlyEventName = 0;
      let hasNeither = 0;
      let wouldCopy = 0;
      let batch = writeBatch(db);
      let ops = 0;
      let committed = 0;

      const commitIfFull = async () => {
        if (ops >= BATCH_LIMIT) {
          if (!dryRun) await batch.commit();
          committed += ops;
          batch = writeBatch(db);
          ops = 0;
        }
      };

      for (const d of snap.docs) {
        scanned++;
        if (d.ref.parent.parent?.parent?.id !== 'users') {
          skippedNonUser++;
          continue;
        }
        const data = d.data() as Record<string, unknown>;
        if (data.isDeleted === true) {
          skippedDeleted++;
          continue;
        }
        const eventSource = typeof data.eventSource === 'string' ? data.eventSource.trim() : '';
        const eventName = typeof data.eventName === 'string' ? data.eventName.trim() : '';

        if (!eventSource && !eventName) {
          hasNeither++;
          continue;
        }
        if (!eventSource && eventName) {
          hasOnlyEventName++;
          continue;
        }
        if (eventSource && eventName) {
          if (eventSource === eventName) hasBothSame++;
          else hasBothDifferent++;
          // If the toggle is on and values agree, we still null eventSource.
          if (alsoNullEventSource && eventSource === eventName) {
            if (!dryRun) batch.update(d.ref, { eventSource: null });
            ops++;
            await commitIfFull();
          }
          continue;
        }

        // eventSource is set, eventName is empty → the main case.
        wouldCopy++;
        if (!dryRun) {
          const patch: Record<string, unknown> = { eventName: eventSource };
          if (alsoNullEventSource) patch.eventSource = null;
          batch.update(d.ref, patch);
        }
        ops++;
        await commitIfFull();
      }

      if (ops > 0 && !dryRun) {
        await batch.commit();
        committed += ops;
      }

      say(
        `${dryRun ? '(dry-run) ' : ''}Scan complete.\n` +
          `  Scanned: ${scanned}\n` +
          `  Skipped non-user: ${skippedNonUser}\n` +
          `  Skipped deleted: ${skippedDeleted}\n` +
          `  No event fields: ${hasNeither}\n` +
          `  Only eventName set: ${hasOnlyEventName}\n` +
          `  Both set, same value: ${hasBothSame}\n` +
          `  Both set, DIFFERENT values (left alone): ${hasBothDifferent}\n` +
          `  ${dryRun ? 'Would copy' : 'Copied'} eventSource → eventName: ${wouldCopy}` +
          `${dryRun ? '' : ` (${committed} writes committed)`}`
      );
      if (hasBothDifferent > 0) {
        say(
          `\nHeads-up: ${hasBothDifferent} item${hasBothDifferent === 1 ? ' has' : 's have'} DIFFERENT values in eventSource vs eventName — left alone. Inspect manually if you want them reconciled.`
        );
      }
    } catch (err) {
      say(`Failed: ${(err as Error).message}`);
    } finally {
      setRunning(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Checking access…
      </div>
    );
  }
  if (!currentUser) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Please sign in.
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        This page is admin-only.
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-glass-muted hover:text-glass mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>

      <h1 className="text-2xl font-bold text-glass mb-2 inline-flex items-center gap-2">
        <RefreshCw className="w-6 h-6" /> Migrate eventSource → eventName
      </h1>
      <p className="text-glass-secondary text-sm mb-3">
        Backfills the January 2026 field rename. Copies{' '}
        <code>eventSource</code> → <code>eventName</code> on user items
        where <code>eventName</code> is empty. Verified against real
        data: both fields hold the same recurring event names ("Polish
        Pick Up", "HHC"). Boxes use a separate <code>boxName</code>
        field — they aren't touched.
      </p>
      <p className="text-glass-muted text-xs mb-6">
        First pass: leave "Also null out eventSource" OFF. That's
        additive-only. After you've verified the app reads eventName
        correctly everywhere, run again with the toggle ON to clean up
        the legacy field.
      </p>

      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4 space-y-3">
        <label className="flex items-center gap-2 text-glass text-sm">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            disabled={running}
          />
          Dry-run (preview counts without writing)
        </label>
        <label className="flex items-start gap-2 text-glass text-sm">
          <input
            type="checkbox"
            checked={alsoNullEventSource}
            onChange={(e) => setAlsoNullEventSource(e.target.checked)}
            disabled={running}
            className="mt-1"
          />
          <span>
            Also null out <code>eventSource</code> on migrated items
            (destructive on the legacy field — only turn on after
            confirming the app no longer reads it).
          </span>
        </label>
        <button
          onClick={run}
          disabled={running}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
        >
          {running ? 'Working…' : dryRun ? 'Preview counts' : 'Commit migration'}
        </button>
      </div>

      {log.length > 0 && (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
          <h3 className="text-glass-secondary text-sm mb-2">Log</h3>
          <pre className="text-xs text-glass-muted whitespace-pre-wrap">
            {log.join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
}
